import { ReactElement } from 'react';
import {
  ESSENTIALS_STEPS,
  formatPriceGbp,
  SCALE_MIN_QUERIES,
} from '@sc/stripe/pricing';
import classNames from 'classnames';

const formatCountGb = (value: number): string =>
  value.toLocaleString('en-GB');

const compactCount = (value: number): string =>
  value >= 1_000 ? `${formatCountGb(value / 1_000)}K` : formatCountGb(value);

/**
 * The synthetic final slider stop past the top Essentials step — "10K+",
 * which resolves to Scale / contact us rather than a purchasable step.
 */
export const PRICING_SLIDER_CONTACT_INDEX = ESSENTIALS_STEPS.length;

const STOP_COUNT = ESSENTIALS_STEPS.length + 1;

const STOP_LABELS = [
  ...ESSENTIALS_STEPS.map((step) => compactCount(step.queries)),
  `${compactCount(SCALE_MIN_QUERIES)}+`,
];

const stopLeft = (index: number): string =>
  `${(index / (STOP_COUNT - 1)) * 100}%`;

/**
 * Stepped slider over the Essentials pricing table with a live price readout:
 * a dot per step on the track and the query volume labelled beneath each.
 * The final stop is "10K+" — Scale territory, shown as contact-us. Controlled:
 * `stepIndex` is an index into ESSENTIALS_STEPS, or
 * PRICING_SLIDER_CONTACT_INDEX for that final stop. Shared by the marketing
 * pricing estimator and the app's change-plan screen.
 */
export const PricingSlider = ({
  stepIndex,
  onChange,
  className,
}: {
  stepIndex: number;
  onChange: (stepIndex: number) => void;
  className?: string;
}): ReactElement => {
  const isContactStop = stepIndex >= PRICING_SLIDER_CONTACT_INDEX;
  const step = isContactStop ? undefined : ESSENTIALS_STEPS[stepIndex];

  return (
    <div className={classNames('flex flex-col gap-6', className)}>
      <div>
        {isContactStop ? (
          <>
            <p className="font-grotesque text-5xl leading-none font-semibold text-white">
              Contact us
            </p>
            <p className="text-sm text-text-low-priority mt-3">
              {formatCountGb(SCALE_MIN_QUERIES)}+ queries every month — custom
              volumes on Scale
            </p>
          </>
        ) : (
          <>
            <p className="font-grotesque text-5xl leading-none font-semibold text-white">
              {formatPriceGbp(step?.priceGbp ?? 0)}
              <span className="text-base leading-6 text-text-low-priority font-normal ml-2">
                / month
              </span>
            </p>
            {step && (
              <p className="text-sm text-text-low-priority mt-3">
                {formatCountGb(step.queries)} queries +{' '}
                {formatCountGb(step.pages)} page imports every month
              </p>
            )}
          </>
        )}
      </div>
      <div>
        <div className="relative h-5">
          {/* A native thumb's centre travels inside the input by half the
              thumb width (18px thumb → 9px each side); the track, fill and
              dots share that coordinate space so the first and last stops
              line up with the thumb at min/max. */}
          <div
            className="absolute top-0 bottom-0 left-2.25 right-2.25"
            aria-hidden="true"
          >
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-0.5 bg-grey-t-700 rounded-full" />
            <div
              className="absolute top-1/2 -translate-y-1/2 left-0 h-0.5 bg-primary-400 rounded-full"
              style={{ width: stopLeft(Math.min(stepIndex, STOP_COUNT - 1)) }}
            />
            {STOP_LABELS.map((label, i) => (
              <span
                key={label}
                className={classNames(
                  'absolute top-1/2 size-2 rounded-full -translate-x-1/2 -translate-y-1/2',
                  i <= stepIndex ? 'bg-primary-400' : 'bg-grey-t-600'
                )}
                style={{ left: stopLeft(i) }}
              />
            ))}
          </div>
          <input
            type="range"
            min={0}
            max={STOP_COUNT - 1}
            step={1}
            value={Math.min(stepIndex, STOP_COUNT - 1)}
            onChange={(e) => onChange(Number(e.target.value))}
            aria-label="Monthly usage"
            aria-valuetext={
              step
                ? `${formatCountGb(step.queries)} queries and ${formatCountGb(
                    step.pages
                  )} page imports for ${formatPriceGbp(
                    step.priceGbp
                  )} per month`
                : `${formatCountGb(
                    SCALE_MIN_QUERIES
                  )} or more queries per month — contact us`
            }
            className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer
              [&::-webkit-slider-runnable-track]:bg-transparent
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:size-4.5
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-primary-400
              [&::-webkit-slider-thumb]:border-2
              [&::-webkit-slider-thumb]:border-white
              [&::-webkit-slider-thumb]:shadow
              [&::-moz-range-track]:bg-transparent
              [&::-moz-range-thumb]:size-4.5
              [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-primary-400
              [&::-moz-range-thumb]:border-2
              [&::-moz-range-thumb]:border-white"
          />
        </div>
        <div className="relative mt-2 h-4 mx-2.25 text-xs text-text-low-priority">
          {STOP_LABELS.map((label, i) => (
            <span
              key={label}
              className={classNames(
                'absolute whitespace-nowrap -translate-x-1/2',
                i === stepIndex && 'text-white font-semibold',
                // Edge labels always show; intermediate labels only fit from
                // sm upwards.
                i !== 0 && i !== STOP_COUNT - 1 && 'hidden sm:block'
              )}
              style={{ left: stopLeft(i) }}
            >
              {label}
            </span>
          ))}
        </div>
        <p className="mt-3 text-sm text-text-low-priority">
          Select monthly queries
        </p>
      </div>
    </div>
  );
};
